import { useEffect } from 'react';
import { ArrowRightIcon, ClockIcon, ExternalLinkIcon, MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { business, routes } from '../data/site';
import { SiteLink } from '../components/SiteLink';
const TITLE = 'Pegasus Spas Location & Hours | Brooklyn, Michigan';
const DESCRIPTION = 'Visit Pegasus Spas at 171 South Main Street in Brooklyn, Michigan. See current store hours, map, directions and what to explore in the showroom.';
const CANONICAL = 'https://www.pegasus-spas.com/location.html';
const EMAIL = 'Hello@Pegasus-Spas.com';
const MAP_SRC = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2960.328431429922!2d-84.24750730000073!3d42.10307639885929!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x883cdfc2c40f5547%3A0x5c0f2bfd8950d9b8!2sPegasus+Spas+%26+Leisure+Products!5e0!3m2!1sen!2s!4v1438939843207';
const DIRECTIONS_URL = 'https://www.google.com/maps/search/?api=1&query=Pegasus+Spas+%26+Leisure+Products%2C+171+South+Main+St%2C+Brooklyn%2C+MI+49230';
const explore = [{
  title: 'Hot Tubs',
  body: 'Compare Sundance Spas, Viking Spas and certified pre-owned possibilities.',
  href: routes.hotTubs,
  linkLabel: 'Explore Hot Tubs'
}, {
  title: 'Outdoor Living',
  body: 'Explore furniture, fire tables, umbrellas, Big Green Egg Grills and hot-tub steps.',
  href: routes.furniture,
  linkLabel: 'Explore Backyard Living'
}, {
  title: 'Replacement Covers',
  body: 'Get help with measurements and use the guided cover builder.',
  href: routes.covers,
  linkLabel: 'Order a Replacement Cover'
}, {
  title: 'Spa Care & Supplies',
  body: 'Find filters, maintenance products and water-care supplies.',
  href: routes.supplies,
  linkLabel: 'Shop Online'
}, {
  title: 'Service Questions',
  body: 'Talk through professional spa service and repair information.',
  href: routes.service,
  linkLabel: 'Spa Service Information'
}];
const checklist = ['Photos and approximate measurements of the intended spa or furniture area', 'Access notes such as gates, steps or narrow pathways', 'A photo of the spa, control panel, filter or part you are asking about. If a model or serial number would help, we can show you where to find it.', 'Questions about care, delivery, placement or financing'];
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
export function LocationPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = TITLE;
    upsertMeta('description', DESCRIPTION);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }, []);
  return <main data-id="element-1579" data-mp="3upqyvbkvffq">
      {/* Compact photo-led hero */}
      <section aria-labelledby="location-h1" className="w-full bg-water" data-id="element-1580" data-mp="3wkgkmxsnvq5">
        <div className="mx-auto max-w-container px-5 py-10 sm:px-8 lg:py-12" data-id="element-1581" data-mp="3lcirmbc80b3y">
          <div className="flex flex-col gap-8 lg:grid lg:grid-cols-12 lg:items-start lg:gap-12" data-id="element-1582" data-mp="3qb3t0e37l0tz">
            <div className="lg:col-span-7" data-id="element-1583" data-mp="htz57pg83zh2">
              <p className="text-[14px] font-bold uppercase text-pegasus-blue" data-id="element-1584" data-mp="hs5yyb9ivh9l">
                Brooklyn Showroom
              </p>
              <h1 id="location-h1" className="mt-2 text-h2-mobile font-semibold text-ink lg:text-[40px] lg:leading-[48px]" data-id="element-1585" data-mp="7wba2yepgpyd">
                Visit Pegasus Spas in Brooklyn, Michigan
              </h1>
              <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-1586" data-mp="2skqr8ncgx5ky">
                See hot tubs, backyard-living products and spa-care essentials in person, then talk
                through your questions with us. We have served the area since 1977.
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-1587" data-mp="390rrthkdnfon">
                <a href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer" aria-label="Get directions to Pegasus Spas in Google Maps (opens in a new tab)" className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-magenta px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-1588" data-mp="342gg11aiuv3s">
                  <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-1589" data-mp="3mv0wfj91b7qp" />
                  Get Directions
                </a>
                <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-bold text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-1590" data-mp="2bdstl2e1dzon">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-1591" data-mp="qv8j9ivrfwfo" />
                  Call or Text {business.phone}
                </a>
              </div>
            </div>

            <div className="lg:col-span-5" data-id="element-1592" data-mp="10ltfx0rviqvy">
              <div className="aspect-[4/3] w-full overflow-hidden border border-ink/15 bg-white" data-id="element-1593" data-mp="10ml8s3u36aij">
                <iframe src={MAP_SRC} title="Map showing Pegasus Spas at 171 South Main Street in Brooklyn, Michigan" loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="h-full w-full border-0" data-id="element-1594" data-mp="15k4o772istc2" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Essential visit details */}
      <section aria-labelledby="essentials-heading" className="w-full bg-white" data-id="element-1595" data-mp="3gfaiq99oezlp">
        <h2 id="essentials-heading" className="sr-only" data-id="element-1596" data-mp="3lcan8fqvnuap">
          Visit details
        </h2>
        <div className="mx-auto max-w-container px-5 py-10 sm:px-8 lg:py-12" data-id="element-1597" data-mp="3dbmjobuowme2">
          <dl className="grid grid-cols-1 gap-x-10 gap-y-7 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1598" data-mp="39b2lr62aleqv">
            <div className="border-t border-ink/15 pt-4" data-id="element-1599" data-mp="3nak1yx1ivjom">
              <MapPinIcon className="h-5 w-5 text-pine" aria-hidden="true" data-id="element-1600" data-mp="3iafnfck6cz11" />
              <dt className="mt-2.5 text-[15px] font-bold uppercase text-ink/60" data-id="element-1601" data-mp="3icil8irkjwgb">Address</dt>
              <dd className="mt-1" data-id="element-1602" data-mp="2zzzdkjgan1">
                <address className="not-italic text-[16px] leading-6 text-ink/85" data-id="element-1603" data-mp="3s8vbt7rthu6u">
                  <span className="block font-semibold text-ink" data-id="element-1604" data-mp="9umh1qjk53xp">{business.name}</span>
                  {business.street}
                  <br data-id="element-1605" data-mp="4v16sjyxt95o" />
                  {business.cityStateZip}
                </address>
              </dd>
            </div>

            <div className="border-t border-ink/15 pt-4" data-id="element-1606" data-mp="jtlf6f6p5h6g">
              <ClockIcon className="h-5 w-5 text-pine" aria-hidden="true" data-id="element-1607" data-mp="etz1y5qvc0xn" />
              <dt className="mt-2.5 text-[15px] font-bold uppercase text-ink/60" data-id="element-1608" data-mp="ermejd2ccyd7">Store hours</dt>
              <dd className="mt-1 space-y-0.5" data-id="element-1609" data-mp="yded8ipw6njn">
                {business.hours.map(entry => <p key={entry.days} className="text-[16px] leading-6 text-ink/85" data-id="element-1610" data-mp="12ezwqmwxm02t">
                    <span className="font-semibold text-ink" data-id="element-1611" data-mp="oh323w21g3qs">{entry.days}</span> {entry.time}
                  </p>)}
              </dd>
            </div>

            <div className="border-t border-ink/15 pt-4" data-id="element-1612" data-mp="eqgq0btj5vcu">
              <PhoneIcon className="h-5 w-5 text-pine" aria-hidden="true" data-id="element-1613" data-mp="ji89k2e9r0th" />
              <dt className="mt-2.5 text-[15px] font-bold uppercase text-ink/60" data-id="element-1614" data-mp="jkcln7mbn64u">Contact</dt>
              <dd className="mt-1" data-id="element-1615" data-mp="5so95385ufnc">
                <p className="text-[16px] leading-6 text-ink/85" data-id="element-1616" data-mp="apoh5joklsh3">
                  Voice/text{' '}
                  <a href={business.phoneHref} className="font-semibold text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1617" data-mp="3rybhm99amric">
                    {business.phone}
                  </a>
                </p>
                <a href={`mailto:${EMAIL}`} className="mt-1 inline-flex min-h-[44px] items-center gap-1.5 break-all text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1618" data-mp="793ez1lug8t8">
                  <MailIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1619" data-mp="3ooa6wenv0ixn" />
                  {EMAIL}
                </a>
              </dd>
            </div>
          </dl>
        </div>
      </section>

      {/* Map + directions */}
      <section aria-labelledby="find-heading" className="w-full border-y border-ink/10 bg-white" data-id="element-1620" data-mp="3apsmapt93mjy">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-1621" data-mp="35q7fjt7ua9wo">
          <div className="grid grid-cols-1" data-id="element-1622" data-mp="2vmsixrlj4llo">
            <div className="max-w-3xl" data-id="element-1623" data-mp="2vlzckrqhfy42">
              <h2 id="find-heading" className="text-h2-mobile font-semibold text-ink lg:text-[32px] lg:leading-[40px]" data-id="element-1624" data-mp="2vp4yjy3tejvk">
                Easy to Find on South Main Street
              </h2>
              <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-1625" data-mp="3akba10lojawa">
                Our showroom is in Brooklyn, Michigan, near the Irish Hills. Use the map for
                your route, and call ahead if you are coming to see a particular product.
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-1626" data-mp="2muqoc9y55zjb">
                <a href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer" aria-label="Open Pegasus Spas in Google Maps (opens in a new tab)" className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-blue px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-ink" data-id="element-1627" data-mp="2msdw93yo68sj">
                  Open in Google Maps
                  <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-1628" data-mp="hpkxh5ar1g1o" />
                </a>
                <SiteLink to={routes.contact} className="inline-flex min-h-[48px] items-center whitespace-nowrap rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-bold text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-1629" data-mp="mra8wm1vvvqa">
                  Contact the Showroom
                </SiteLink>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What to explore */}
      <section aria-labelledby="explore-heading" className="w-full bg-water" data-id="element-1630" data-mp="7usypy5fkn5b">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1631" data-mp="37fojky58c7rc">
          <h2 id="explore-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1632" data-mp="3bhtavq87gsat">
            What You Can Explore at the Showroom
          </h2>

          <ul className="mt-7 max-w-4xl border-t border-ink/20" data-id="element-1633" data-mp="2yz7hrl44k2ne">
            {explore.map(item => <li key={item.title} className="flex flex-col gap-1 border-b border-ink/15 py-4 sm:flex-row sm:items-baseline sm:justify-between sm:gap-8" data-id="element-1634" data-mp="2uxcgggm71p9m">
                <div data-id="element-1635" data-mp="2l07pm3hel5mh">
                  <h3 className="text-[17.5px] font-semibold leading-7 text-ink" data-id="element-1636" data-mp="2l0pt9l6eam4f">{item.title}</h3>
                  <p className="mt-0.5 text-[16px] leading-6 text-ink/75" data-id="element-1637" data-mp="2g8g0zyht59yz">{item.body}</p>
                </div>
                <SiteLink to={item.href} className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 whitespace-nowrap text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1638" data-mp="2bao9v6ozlrvu">
                  {item.linkLabel}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1639" data-mp="1noude2ytntfe" />
                </SiteLink>
              </li>)}
          </ul>

          <p className="mt-6 max-w-3xl text-[16px] leading-6 text-ink/75" data-id="element-1640" data-mp="1dpd83mnhfhjy">
            Display models, brands and in-store availability can change. Call or text before visiting
            if you need to see a specific item.
          </p>
        </div>
      </section>

      {/* Plan a useful visit */}
      <section aria-labelledby="plan-heading" className="w-full bg-white" data-id="element-1641" data-mp="2bclo524pryek">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1642" data-mp="1wnpgv7ahepf0">
          <h2 id="plan-heading" className="max-w-3xl text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1643" data-mp="1wllfaw0222hw">
            Bring the Details That Help
          </h2>
          <p className="mt-4 max-w-3xl text-body text-ink/80 sm:text-body-lg" data-id="element-1644" data-mp="2paa7u6n1ozi1">
            A little context helps us give more practical guidance while you are in the
            showroom.
          </p>

          <ul className="mt-6 max-w-3xl list-disc space-y-2 border-t border-ink/15 pl-6 pt-5" data-id="element-1645" data-mp="dorlaowjpvq7">
            {checklist.map(item => <li key={item} className="text-[16.5px] leading-6 text-ink/85" data-id="element-1646" data-mp="sdxlaf37jpz5">
                {item}
              </li>)}
          </ul>
        </div>
      </section>

      {/* Local service area */}
      <section aria-labelledby="area-heading" className="w-full border-y border-ink/10 bg-white" data-id="element-1647" data-mp="3vv5xaib97vly">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1648" data-mp="3qx4fe0pke2c2">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12" data-id="element-1649" data-mp="8qgc8ge2rp91">
            <div className="lg:col-span-7" data-id="element-1650" data-mp="4ntq8w434yjr">
              <h2 id="area-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1651" data-mp="4ms5u116izpd">
                A Brooklyn Showroom for the Surrounding Area
              </h2>
              <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-1652" data-mp="2bdolz1x64hab">
                We serve local families in Brooklyn and communities throughout Jackson,
                Lenawee, Hillsdale and Washtenaw counties, along with surrounding areas.
              </p>
              <p className="mt-3 text-[16px] leading-6 text-ink/70" data-id="element-1653" data-mp="2kg6ajn0apm0h">
                Delivery and service availability varies by location.
              </p>
            </div>

            <div className="lg:col-span-5" data-id="element-1654" data-mp="2ubhqu9rcr1ac">
              <ul className="border-t border-ink/15 pt-4" data-id="element-1655" data-mp="2yduvsvy9yv62">
                {[{
                label: 'Why Pegasus',
                href: routes.about
              }, {
                label: 'Read Customer Reviews',
                href: routes.reviews
              }].map(link => <li key={link.label} data-id="element-1656" data-mp="382cu96j30q6j">
                    <SiteLink to={link.href} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1657" data-mp="3d2r14l8y5u2z">
                      {link.label}
                      <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1658" data-mp="wgcbgs5x786s" />
                    </SiteLink>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Final navy CTA */}
      <section aria-labelledby="plan-visit-heading" className="w-full bg-ink" data-id="element-1659" data-mp="1p4gibh089rb2">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1660" data-mp="1k6y7tm6kb40y">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12" data-id="element-1661" data-mp="1feoj29h6oa09">
            <div className="lg:col-span-7" data-id="element-1662" data-mp="1ag3keehkxydx">
              <p className="text-[14px] font-bold uppercase text-white/70" data-id="element-1663" data-mp="1af24nwpmcnpv">Come See Us</p>
              <h2 id="plan-visit-heading" className="mt-2 text-h2-mobile font-semibold text-white lg:text-h2-desktop" data-id="element-1664" data-mp="27wj2v3fzprql">
                Plan Your Showroom Visit
              </h2>
              <p className="mt-4 text-body text-white/85 sm:text-body-lg" data-id="element-1665" data-mp="17qto19wv5gh">
                Stop in during regular store hours or call ahead with a product-specific question.
              </p>

              <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-1666" data-mp="3nma8fz2ssmnh">
                <a href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer" aria-label="Get directions to Pegasus Spas in Google Maps (opens in a new tab)" className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-1667" data-mp="3sl4myotqfp1s">
                  <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-1668" data-mp="12wkrngijaqs5" />
                  Get Directions
                </a>
                <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn border-2 border-white/70 px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-ink" data-id="element-1669" data-mp="17vxfo4l563wb">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-1670" data-mp="324b2crbdwqal" />
                  Call or Text {business.phone}
                </a>
              </div>
            </div>

            <div className="lg:col-span-5" data-id="element-1671" data-mp="3b3o9hdc1d2s5">
              <address className="not-italic" data-id="element-1672" data-mp="3b2cyasg9gsr0">
                <p className="text-[17px] font-semibold leading-7 text-white" data-id="element-1673" data-mp="365mhg2wmtt4j">{business.street}</p>
                <p className="text-[17px] leading-7 text-white/85" data-id="element-1674" data-mp="3l2x3g66wihei">{business.cityStateZip}</p>
              </address>
              <dl className="mt-4 space-y-1 border-t border-white/20 pt-4" data-id="element-1675" data-mp="3g4c223fwxroh">
                {business.hours.map(entry => <div key={entry.days} className="flex flex-wrap gap-x-2 text-[16px] leading-6" data-id="element-1676" data-mp="1qomdtvihfj7o">
                    <dt className="font-semibold text-white" data-id="element-1677" data-mp="1lr425xd2ypvz">{entry.days}</dt>
                    <dd className="text-white/85" data-id="element-1678" data-mp="zv2dlgs3bczn">{entry.time}</dd>
                  </div>)}
              </dl>
            </div>
          </div>
        </div>
      </section>
    </main>;
}